import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { RoundName } from "../../domain/round/types";
import { ApiError } from "../../lib/api";
import { preparationApi, type SetupStaff, type TournamentSetupPayload } from "../../lib/preparationApi";

const ROUNDS: RoundName[] = ["R1", "R2", "R3", "R4"];
const ROLE_OPTIONS = [
  { value: "off", label: "Wolne" },
  { value: "scorer", label: "Scorer" },
  { value: "setup", label: "Setup" },
  { value: "dm", label: "Data Manager / Control" },
];
const EARLY_RETURN_OPTIONS = [
  { value: "", label: "Brak priorytetu" },
  { value: "low", label: "Niski · jeśli łatwo się da" },
  { value: "high", label: "Wysoki · celuj w 1–2 falę" },
  { value: "critical", label: "Krytyczny · pierwsza możliwa fala" },
];

type Constraint = {
  availableFrom?: string;
  finishBy?: string;
  fixedGroup?: number;
  fixedKind?: string;
  no27?: boolean;
  only9?: boolean;
  earlyReturnPriority?: "" | "low" | "high" | "critical";
  mustFinishFirst?: boolean;
};

type Draft = {
  baseId: string;
  roomName: string;
  driverEligible: boolean;
  tvEligible: boolean;
  roles: Record<RoundName, string>;
  notes: string;
  roundConstraints: Record<string, Constraint>;
};

function normalizedConstraints(value: Record<string, unknown> | undefined): Record<string, Constraint> {
  const result: Record<string, Constraint> = {};
  for (const round of ROUNDS) {
    const raw = value?.[round];
    if (raw && typeof raw === "object") result[round] = { ...(raw as Constraint) };
  }
  return result;
}

function initialDraft(person: SetupStaff): Draft {
  return {
    baseId: person.baseId,
    roomName: person.roomName || "",
    driverEligible: person.driverEligible,
    tvEligible: person.tvEligible,
    roles: { ...person.roles },
    notes: person.notes,
    roundConstraints: normalizedConstraints(person.roundConstraints),
  };
}

function hasConstraint(value: Constraint | undefined) {
  return Boolean(value && (value.availableFrom || value.finishBy || value.fixedGroup || value.no27 || value.only9 || value.earlyReturnPriority || value.mustFinishFirst));
}

export function StaffEditorDrawer({
  tournamentId,
  data,
  person,
  onClose,
}: {
  tournamentId: string;
  data: TournamentSetupPayload;
  person: SetupStaff;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => initialDraft(person));
  const [constraintRound, setConstraintRound] = useState<RoundName>("R1");
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    setDraft(initialDraft(person));
    setConfirmRemove(false);
  }, [person]);

  const refreshDependentViews = () => {
    void queryClient.invalidateQueries({ queryKey: ["round-workspace", tournamentId] });
    void queryClient.invalidateQueries({ queryKey: ["generator-preflight", tournamentId] });
  };

  const save = useMutation({
    mutationFn: () => preparationApi.saveStaffProfile(tournamentId, person.personId, {
      expectedRevision: data.configRevision,
      baseId: draft.baseId,
      roomName: draft.roomName,
      driverEligible: draft.driverEligible,
      tvEligible: draft.tvEligible,
      roles: draft.roles,
      notes: draft.notes,
      roundConstraints: draft.roundConstraints,
    }),
    onSuccess: (next) => {
      queryClient.setQueryData(["tournament-setup", tournamentId], next);
      refreshDependentViews();
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => preparationApi.removeStaff(tournamentId, {
      expectedRevision: data.configRevision,
      personIds: [person.personId],
    }),
    onSuccess: (result) => {
      queryClient.setQueryData(["tournament-setup", tournamentId], result.setup);
      refreshDependentViews();
      onClose();
    },
  });

  const currentConstraint = draft.roundConstraints[constraintRound] || {};
  const setConstraint = (patch: Partial<Constraint>) => setDraft((current) => {
    const next = { ...(current.roundConstraints[constraintRound] || {}), ...patch };
    const priority = ["low", "high", "critical"].includes(String(next.earlyReturnPriority || ""))
      ? String(next.earlyReturnPriority) as "low" | "high" | "critical"
      : "";
    const mustFinishFirst = Boolean(next.mustFinishFirst);
    const clean: Constraint = {
      availableFrom: String(next.availableFrom || ""),
      finishBy: String(next.finishBy || ""),
      fixedGroup: mustFinishFirst ? undefined : Number(next.fixedGroup || 0) || undefined,
      fixedKind: mustFinishFirst ? "" : next.fixedGroup ? String(next.fixedKind || "full") : "",
      no27: Boolean(next.no27),
      only9: Boolean(next.only9),
      earlyReturnPriority: priority,
      mustFinishFirst,
    };
    const active = Boolean(clean.availableFrom || clean.finishBy || clean.fixedGroup || clean.no27 || clean.only9 || clean.earlyReturnPriority || clean.mustFinishFirst);
    const roundConstraints = { ...current.roundConstraints };
    if (active) roundConstraints[constraintRound] = clean;
    else delete roundConstraints[constraintRound];
    return { ...current, roundConstraints };
  });

  const mutationError = save.error || remove.error;
  const error = mutationError instanceof ApiError ? mutationError.message : mutationError instanceof Error ? mutationError.message : "";
  const dirty = JSON.stringify(draft) !== JSON.stringify(initialDraft(person));
  const busy = save.isPending || remove.isPending;

  return (
    <div className="prep-drawer-layer" role="presentation">
      <button className="prep-drawer-backdrop" type="button" aria-label="Zamknij edytor" onClick={onClose} />
      <aside className="prep-drawer staff-editor-wide" aria-label={`Edycja ${person.displayName}`}>
        <header>
          <div><span className="eyebrow">TOURNAMENT STAFF</span><h3>{person.displayName}</h3><p>{person.employeeId || person.sourceDocumentId}</p></div>
          <button type="button" className="prep-drawer-close" onClick={onClose}>×</button>
        </header>

        <section className="prep-editor-section">
          <span className="prep-editor-label">ROLA W RUNDACH</span>
          <div className="staff-role-editor-grid">
            {ROUNDS.map((round) => (
              <label key={round}>
                <strong>{round}</strong>
                <select value={draft.roles[round] || "off"} onChange={(event) => setDraft((value) => ({ ...value, roles: { ...value.roles, [round]: event.target.value } }))}>
                  {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            ))}
          </div>
        </section>

        <section className="prep-editor-section">
          <span className="prep-editor-label">NOCLEG / POKÓJ / UPRAWNIENIA</span>
          <label className="prep-field"><span>Nocleg</span><select value={draft.baseId} onChange={(event) => setDraft((value) => ({ ...value, baseId: event.target.value }))}><option value="">—</option>{data.accommodations.map((base) => <option key={base.id} value={base.id}>{base.name}</option>)}</select></label>
          <label className="prep-field"><span>Pokój</span><input value={draft.roomName} onChange={(event) => setDraft((value) => ({ ...value, roomName: event.target.value }))} placeholder="np. Twin room 12 / 204" /></label>
          <p className="prep-editor-help">Osoby z identyczną nazwą pokoju są traktowane przez solver jako współlokatorzy — preferowany jest wspólny albo następny transport rano.</p>
          <div className="staff-editor-toggles">
            <button type="button" className={draft.driverEligible ? "active driver" : ""} onClick={() => setDraft((value) => ({ ...value, driverEligible: !value.driverEligible }))}><strong>Kierowca</strong><span>{draft.driverEligible ? "TAK" : "NIE"}</span></button>
            <button type="button" className={draft.tvEligible ? "active tv" : "no-tv"} onClick={() => setDraft((value) => ({ ...value, tvEligible: !value.tvEligible }))}><strong>Może TV</strong><span>{draft.tvEligible ? "TAK" : "NIE"}</span></button>
          </div>
        </section>

        <section className="prep-editor-section staff-constraints-section">
          <div className="staff-constraints-head"><div><span className="prep-editor-label">WYJĄTKI DLA RUNDY</span><strong>{ROUNDS.filter((round) => hasConstraint(draft.roundConstraints[round])).length} rund z wyjątkami</strong></div><div>{ROUNDS.map((round) => <button type="button" key={round} className={constraintRound === round ? "active" : ""} onClick={() => setConstraintRound(round)}>{round}{hasConstraint(draft.roundConstraints[round]) ? " •" : ""}</button>)}</div></div>

          <label className="check-line staff-no27">
            <input type="checkbox" checked={Boolean(currentConstraint.mustFinishFirst)} onChange={(event) => setConstraint({ mustFinishFirst: event.target.checked })} />
            <strong>MUSI SKOŃCZYĆ PIERWSZY w {constraintRound}</strong>
          </label>
          {currentConstraint.mustFinishFirst ? <p className="prep-editor-help">TWARDY WARUNEK: przed solverem system sam wybierze najwcześniej kończącą grupę (przy dwóch tee rozłoży oznaczone osoby między tee), narzuci pełne 18 + zakaz 27 dołków, a po solverze odrzuci każdy wynik, który tego nie spełni. Nie trzeba wpisywać numeru grupy ręcznie.</p> : null}

          <div className="staff-constraint-grid">
            <label className="prep-field"><span>Dostępny od</span><input type="time" value={currentConstraint.availableFrom || ""} onChange={(event) => setConstraint({ availableFrom: event.target.value })} /></label>
            <label className="prep-field"><span>Musi skończyć do</span><input type="time" value={currentConstraint.finishBy || ""} onChange={(event) => setConstraint({ finishBy: event.target.value })} /></label>
            <label className="prep-field"><span>Priorytet wcześniejszego powrotu</span><select value={currentConstraint.earlyReturnPriority || ""} onChange={(event) => setConstraint({ earlyReturnPriority: event.target.value as Constraint["earlyReturnPriority"] })}>{EARLY_RETURN_OPTIONS.map((option) => <option key={option.value || "none"} value={option.value}>{option.label}</option>)}</select></label>
            <label className="prep-field"><span>Narzucona grupa</span><input type="number" min={1} max={200} disabled={Boolean(currentConstraint.mustFinishFirst)} value={currentConstraint.fixedGroup || ""} onChange={(event) => setConstraint({ fixedGroup: Number(event.target.value) || undefined })} placeholder={currentConstraint.mustFinishFirst ? "AUTO · pierwsza grupa" : "np. 14"} /></label>
            <label className="prep-field"><span>Pozycja w narzuconej grupie</span><select disabled={Boolean(currentConstraint.mustFinishFirst) || !currentConstraint.fixedGroup} value={currentConstraint.fixedKind || "full"} onChange={(event) => setConstraint({ fixedKind: event.target.value })}><option value="full">Pełne 18</option><option value="h1">1–9</option><option value="h2">10–18</option></select></label>
          </div>
          {currentConstraint.earlyReturnPriority ? <p className="prep-editor-help">Zwykły priorytet powrotu jest miękki. Do sytuacji bezwzględnych użyj pola „MUSI SKOŃCZYĆ PIERWSZY”.</p> : null}
          <label className="check-line staff-no27"><input type="checkbox" checked={Boolean(currentConstraint.no27)} onChange={(event) => setConstraint({ no27: event.target.checked })} /> Zakaz 27 dołków w {constraintRound}</label>
          <label className="check-line staff-no27"><input type="checkbox" checked={Boolean(currentConstraint.only9)} onChange={(event) => setConstraint({ only9: event.target.checked })} /> Tylko 9 dołków w {constraintRound} (awaryjnie: jedna połówka dnia)</label>
          <button type="button" className="text-button" disabled={!hasConstraint(currentConstraint)} onClick={() => setDraft((current) => { const roundConstraints = { ...current.roundConstraints }; delete roundConstraints[constraintRound]; return { ...current, roundConstraints }; })}>Wyczyść wyjątki {constraintRound}</button>
        </section>

        <section className="prep-editor-section">
          <label className="prep-field"><span>Notatka</span><textarea value={draft.notes} onChange={(event) => setDraft((value) => ({ ...value, notes: event.target.value }))} placeholder="Opcjonalna notatka operacyjna" /></label>
        </section>

        {confirmRemove ? (
          <section className="staff-remove-confirm" aria-live="polite">
            <div><strong>Usunąć {person.displayName} tylko z tego turnieju?</strong><span>Osoba zostanie w globalnej bazie pracowników i będzie można dodać ją ponownie.</span></div>
            <div><button type="button" className="secondary-button" disabled={busy} onClick={() => setConfirmRemove(false)}>Nie usuwaj</button><button type="button" className="staff-remove-button" disabled={busy} onClick={() => remove.mutate()}>{remove.isPending ? "Usuwam…" : "Tak, usuń z turnieju"}</button></div>
          </section>
        ) : null}

        {error ? <div className="prep-editor-error">{error}</div> : null}
        <footer className="staff-editor-footer">
          <button type="button" className="staff-remove-link" disabled={busy} onClick={() => setConfirmRemove(true)}>Usuń z turnieju</button>
          <div>
            <button type="button" className="secondary-button" disabled={busy} onClick={onClose}>Anuluj</button>
            <button type="button" className="primary-button" disabled={!dirty || busy} onClick={() => save.mutate()}>{save.isPending ? "Zapisuję…" : "Zapisz zmiany"}</button>
          </div>
        </footer>
      </aside>
    </div>
  );
}
